import { Box, Divider, ToggleButton, ToggleButtonGroup, Tooltip, alpha } from '@mui/material'
import {
  CallMerge as CallMergeIcon,
  CallSplit as CallSplitIcon,
  CropFree as CropFreeIcon,
  Flight as FlightIcon,
} from '@mui/icons-material'

import { CLOCK_THEMES } from './MapClock'
import { glassSx } from './overlayStyles'

const ACCENT = CLOCK_THEMES.live.accent

// Hints match MapEvents: Merge and every Mark button work by dragging a rectangle over targets;
// Unmerge works by clicking one target. After one action the map goes back to Map mode.
const RECT_HINT = 'Drag a rectangle on the map around the targets'
const ONCE_HINT = 'Returns to Map mode afterwards.'

const TOOLS = [
  { value: 'map', label: 'Map', Icon: CropFreeIcon, hint: 'Move and zoom the map, click a target to select it.' },
  { value: 'merge', label: 'Merge', Icon: CallMergeIcon, hint: `${RECT_HINT} to merge them into one track.` },
  { value: 'unmerge', label: 'Unmerge', Icon: CallSplitIcon, hint: 'Click a merged target to split it back into its tracks.' },
]

const STATUSES = [
  { value: 'status_enemy', label: 'Enemy', filter: 'enemy' },
  { value: 'status_friendly', label: 'Friendly', filter: 'friendly' },
  { value: 'status_pass', label: 'Pass', filter: 'pass' },
  { value: 'status_unknown', label: 'Unknown', filter: 'unknown' },
  { value: 'status_important', label: 'Important', filter: 'important' },
]

const TipTitle = ({ title, hint, once }) => (
  <Box sx={{ py: 0.25, maxWidth: 240 }}>
    <Box sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.25 }}>{title}</Box>
    <Box sx={{ fontSize: '0.72rem', opacity: 0.85 }}>{hint}</Box>
    {once && <Box sx={{ fontSize: '0.68rem', opacity: 0.6, mt: 0.25 }}>{ONCE_HINT}</Box>}
  </Box>
)

// Icon-only buttons: the bar stays small so it covers as few live targets as possible
const groupSx = {
  gap: 0.25,
  '& .MuiToggleButton-root': {
    width: 32,
    height: 32,
    p: 0,
    border: 'none',
    borderRadius: '8px !important',
    color: 'rgba(255,255,255,0.85)',
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: ACCENT,
      backgroundColor: alpha(ACCENT, 0.2),
      boxShadow: `inset 0 0 0 1px ${alpha(ACCENT, 0.7)}`,
    },
  },
}

// Admin tools at the bottom of the Radar map: how clicks on the map behave (map / merge /
// unmerge) and which status a clicked target gets. Both are one value: mapMode.
// filters: CSS filter settings per status, the same ones that color the targets on the map.
const RadarToolbar = ({ mapMode, onModeChange, filters, buildIconStyle }) => {
  const tool = TOOLS.some((t) => t.value === mapMode) ? mapMode : null
  const status = typeof mapMode === 'string' && mapMode.startsWith('status_') ? mapMode : null

  const onChange = (_, value) => {
    if (value) onModeChange(value)
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.4,
        borderRadius: '10px',
        ...glassSx(ACCENT, { interactive: true }),
      }}
    >
      <ToggleButtonGroup value={tool} exclusive onChange={onChange} size="small" aria-label="map tool" sx={groupSx}>
        {TOOLS.map(({ value, label, Icon, hint }) => (
          <Tooltip key={value} title={<TipTitle title={label} hint={hint} once={value !== 'map'} />} placement="top" arrow>
            <ToggleButton value={value} aria-label={label}>
              <Icon sx={{ fontSize: '1.15rem' }} />
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.18)' }} />

      <ToggleButtonGroup value={status} exclusive onChange={onChange} size="small" aria-label="target status" sx={groupSx}>
        {STATUSES.map(({ value, label, filter }) => (
          <Tooltip
            key={value}
            title={<TipTitle title={`Mark as ${label}`} hint={`${RECT_HINT} to mark them all as ${label.toLowerCase()}.`} once />}
            placement="top"
            arrow
          >
            <ToggleButton value={value} aria-label={`Mark as ${label}`}>
              {filter === 'important' ? (
                // Ring + plane, both tinted by the filter, like the important marker on the map
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '2px solid #fff',
                    ...buildIconStyle(filters.important),
                  }}
                >
                  <FlightIcon sx={{ fontSize: '0.8rem' }} />
                </Box>
              ) : (
                <FlightIcon sx={{ fontSize: '1.15rem', ...buildIconStyle(filters[filter]) }} />
              )}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  )
}

export default RadarToolbar
